import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const organizationOptions = [
  "Non-Profit Organization",
  "For-Profit Organization"
];

const profitOptions = [
  "Yes",
  "No"
];

const ProfitStep = ({ onNext, initialValue = {
  organizationType: "",
  isProfitable: "",
  profitAmount: ""
} }) => {
  const [formData, setFormData] = useState(initialValue);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Reset profit-related fields when organization type changes
      if (field === 'organizationType') {
        newData.isProfitable = "";
        newData.profitAmount = "";
      }
      
      // Reset profit amount when profitability changes
      if (field === 'isProfitable') {
        newData.profitAmount = "";
      }
      
      return newData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.organizationType) {
      newErrors.organizationType = 'Please select your organization type';
    }

    if (formData.organizationType === "For-Profit Organization") {
      if (!formData.isProfitable) {
        newErrors.isProfitable = 'Please indicate if you made a profit';
      }
      
      if (formData.isProfitable === "Yes" && !formData.profitAmount) {
        newErrors.profitAmount = 'Please enter your profit amount';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onNext(formData);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col min-h-[400px] md:min-h-[450px]"
    >
      <div className="space-y-3 mb-6">
        <div className="text-center space-y-1">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl md:text-3xl font-bold text-gray-900"
          >
            Are you a non-profit organization?
          </motion.h2>
          <div className="w-12 h-0.5 bg-[#1a4a1a] mx-auto"></div>
        </div>
      </div>

      <div className="flex-grow space-y-6">
        <div className="space-y-4">
          <RadioGroup
            value={formData.organizationType}
            onValueChange={(value) => handleChange('organizationType', value)}
            className="grid grid-cols-1 gap-4 max-w-xl mx-auto"
          >
            {organizationOptions.map((option) => (
              <div key={option}>
                <RadioGroupItem
                  value={option}
                  id={option}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={option}
                  className="flex items-center justify-center rounded-lg border-2 border-slate-200 bg-white p-4 md:p-5 hover:bg-[#1a4a1a]/5 hover:border-[#1a4a1a]/20 peer-data-[state=checked]:border-[#1a4a1a] peer-data-[state=checked]:bg-[#1a4a1a]/5 [&:has([data-state=checked])]:border-[#1a4a1a] cursor-pointer transition-all duration-200 text-base md:text-lg font-medium h-[60px] md:h-[64px]"
                >
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
          {errors.organizationType && (
            <p className="text-red-500 text-sm text-center">{errors.organizationType}</p>
          )}
        </div>

        {formData.organizationType === "For-Profit Organization" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg md:text-xl font-semibold text-gray-900">
                  Did you earn a profit last year?
                </h3>
              </div>
              <RadioGroup
                value={formData.isProfitable}
                onValueChange={(value) => handleChange('isProfitable', value)}
                className="grid grid-cols-2 gap-4 max-w-md mx-auto"
              >
                {profitOptions.map((option) => (
                  <div key={option}>
                    <RadioGroupItem
                      value={option}
                      id={`profit-${option}`}
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor={`profit-${option}`}
                      className="flex items-center justify-center rounded-lg border-2 border-slate-200 bg-white p-4 md:p-5 hover:bg-[#1a4a1a]/5 hover:border-[#1a4a1a]/20 peer-data-[state=checked]:border-[#1a4a1a] peer-data-[state=checked]:bg-[#1a4a1a]/5 [&:has([data-state=checked])]:border-[#1a4a1a] cursor-pointer transition-all duration-200 text-base md:text-lg font-medium h-[60px] md:h-[64px]"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {errors.isProfitable && (
                <p className="text-red-500 text-sm text-center">{errors.isProfitable}</p>
              )}
            </div>

            {formData.isProfitable === "Yes" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2"
              >
                <Label htmlFor="profitAmount" className="text-base text-gray-700 text-center block">
                  How much profit did you make last year?
                </Label>
                <div className="max-w-md mx-auto">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-base md:text-lg">$</span>
                    </div>
                    <Input
                      id="profitAmount"
                      type="text"
                      value={formData.profitAmount}
                      onChange={(e) => handleChange('profitAmount', e.target.value)}
                      placeholder="Enter amount"
                      className={`pl-7 w-full text-base md:text-lg py-6 ${errors.profitAmount ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.profitAmount && (
                    <p className="text-red-500 text-sm mt-1">{errors.profitAmount}</p>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>

      <div className="mt-6">
        <Button
          onClick={handleSubmit}
          disabled={!formData.organizationType || (formData.organizationType === "For-Profit Organization" && !formData.isProfitable)}
          className="w-full bg-[#1a4a1a] hover:bg-[#1a4a1a]/90 text-white py-3 md:py-4 text-base md:text-lg font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
        >
          Continue
        </Button>
      </div>
    </motion.div>
  );
};

export default ProfitStep; 